import api from '../api/client';
import React, { useEffect, useState } from 'react';
import { Select, Card, Button, message, Row, Col, Space, Modal, Form, Alert, Tag } from 'antd';
import EggSaleForm from './EggSaleForm';
import AnimalSaleForm from './AnimalSaleForm';
import { AnimalReceiptPreview, ReceiptPreview } from './ReceiptPreview';
import axios from 'axios';
import config from '../config';
import UnifiedSalesForm from './UnifiedSalesForm';
import { Link } from 'react-router-dom';
import { BackwardFilled, BackwardOutlined } from '@ant-design/icons';

export default function SalesPage() {
    const [saleType, setSaleType] = useState(null);
    const [formData, setFormData] = useState(null);
    const [farms, setFarms] = useState([]);
    const [stakeholders, setStakeholders] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                const response = await api.get(`/farm`);
                const data = response.data;
                setFarms(data);
            } catch (error) {
                console.error('Error fetching suppliers:', error);
                message.error('Failed to load farms');
            }
        };

        fetchSuppliers();
    }, []);

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                const response = await api.get(`/customer`);
                const data = response.data;
                setStakeholders(data);
            } catch (error) {
                console.error('Error fetching suppliers:', error);
                message.error('Failed to load customers');
            }
        };

        fetchSuppliers();
    }, []);

    useEffect(() => {
        const fetchDelivery = async () => {
            try {
                const response = await api.get(`/users`);
                const data = response.data;
                setEmployees(data);
            } catch (error) {
                console.error('Error fetching Delivery:', error);
                message.error('Failed to load employees');
            }
        };

        fetchDelivery();
    }, []);

    // Validate form data before submission
    const validateFormData = (data, type) => {
        const errors = [];
        if (!data) {
            errors.push('No sale data found');
            return errors;
        }

        // Common validations for all sale types
        if (!data.stakeholderId) {
            errors.push('Customer is required');
        }
        if (!data.farmId) {
            errors.push('Farm is required');
        }

        if (!data.saleDate) {
            errors.push('Sale date is required');
        }

        // Delivery validation
        if (data.deliveredMethod === 'delivered' && !data.deliveryPersonId) {
            errors.push('Delivery person is required when delivery method is "Delivered"');
        }

        // Items validation
        if (!data.items || data.items.length === 0) {
            errors.push('At least one sale item is required');
        } else {
            // Validate each item based on sale type
            data.items.forEach((item, index) => {
                if (type === 'eggs') {
                    if (!item.type) {
                        errors.push(`Item ${index + 1}: Egg type is required`);
                    }
                    if (!item.trays || item.trays <= 0) {
                        errors.push(`Item ${index + 1}: Number of trays must be greater than 0`);
                    }
                    if (!item.pricePerTray || item.pricePerTray <= 0) {
                        errors.push(`Item ${index + 1}: Price per tray must be greater than 0`);
                    }
                } else if (type === 'animals') {
                    if (!item.species) {
                        errors.push(`Item ${index + 1}: Animal species is required`);
                    }
                    if (!item.category) {
                        errors.push(`Item ${index + 1}: Animal category is required`);
                    }
                    if (!item.identifier) {
                        errors.push(`Item ${index + 1}: Animal identifier is required`);
                    }
                    if (!item.quantity || item.quantity <= 0) {
                        errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
                    }
                    if (!item.weight || item.weight <= 0) {
                        errors.push(`Item ${index + 1}: Weight (KGs) must be greater than 0`);
                    }
                    if (!item.pricePerAnimal || item.pricePerAnimal <= 0) {
                        errors.push(`Item ${index + 1}: Price per animal must be greater than 0`);
                    }
                }
            });
        }

        // Total amount validation
        if (!data.totalAmount || data.totalAmount <= 0) {
            errors.push('Total amount must be greater than 0');
        }

        return errors;
    };

    const getFormStatus = () => {
        if (!formData) return { status: 'empty', message: 'No data entered' };

        const errors = validateFormData(formData, saleType);
        if (errors.length > 0) {
            return { status: 'error', message: `${errors.length} validation error(s)` };
        }

        return { status: 'success', message: 'All required fields are complete' };
    };

    const formStatus = getFormStatus();

    return (
        <Row gutter={20}>
            <Col span={24}>
                <Card
                    title="Record New Sale"
                    extra={
                        <>
                            <Tag color={formStatus.status === 'success' ? 'green' : formStatus.status === 'error' ? 'red' : 'orange'}>
                                {formStatus.message}
                            </Tag>
                            <Link to="/sales">
                                <Button type="default" size="small" icon={<BackwardOutlined />}>
                                    Back to Sales
                                </Button>
                            </Link>
                        </>
                    }
                >
                    {validationErrors.length > 0 && (
                        <Alert
                            message="Validation Errors"
                            description={
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {validationErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <UnifiedSalesForm
                        employees={employees}
                        stakeholders={stakeholders}
                        farms={farms}
                        onChange={(data) => {
                            setFormData(data);
                            setValidationErrors([]);
                        }}
                    />
                </Card>
            </Col>
        </Row>
    );
}
